import React, { useEffect, useRef, useState } from 'react';
import Button from 'view/components/buttons/Button';
import ConfirmationModal from './ConfirmationModal';
import { faMicrophone, faPauseCircle } from '@fortawesome/free-solid-svg-icons';

interface AudioRecorderProps {
  startConfirmationMessage?: string;
  endConfirmationMessage?: string;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onSubmit: (audioBlob: Blob) => void;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  startConfirmationMessage,
  endConfirmationMessage,
  onStartRecording,
  onStopRecording,
  onSubmit,
}) => {
  const [canStart, setCanStart] = useState<boolean>(false);
  const [canEnd, setCanEnd] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    if (navigator.mediaDevices) {
      const constraints = { audio: true };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          streamRef.current = stream;
          const mediaRecorder = new MediaRecorder(stream);
          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.ondataavailable = (e: BlobEvent) => {
            chunksRef.current.push(e.data);
          };

          mediaRecorder.onstop = () => {
            console.debug('stopping');
            const blob = new Blob(chunksRef.current, {
              type: 'audio/ogg; codecs=opus',
            });
            chunksRef.current = [];
            onSubmit(blob);
          };
        })
        .catch((err) => {
          console.error(`The following error occurred: ${err}`);
        });
    } else {
      console.error('getUserMedia not supported on your browser!');
    }

    // Cleanup: stop all tracks when component unmounts
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line
  }, []);

  const startRecording = () => {
    if (!canStart && startConfirmationMessage) {
      setShowConfirmationModal(true);
    } else if (
      mediaRecorderRef.current &&
      (canStart || !startConfirmationMessage)
    ) {
      mediaRecorderRef.current.start();
      setIsRecording(true);
      onStartRecording();
    }
  };

  const stopRecording = () => {
    if (!canEnd && endConfirmationMessage) {
      setShowConfirmationModal(true);
    } else if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive' &&
      (canEnd || !endConfirmationMessage)
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      onStopRecording();
    }
  };

  const handleConfirmationModalConfirm = () => {
    if (isRecording) {
      setCanEnd(true);
    } else {
      setCanStart(true);
    }
    setShowConfirmationModal(false);
  };

  const handleConfirmationModalCancel = () => {
    if (isRecording) {
      setCanEnd(false);
    } else {
      setCanStart(false);
    }
    setShowConfirmationModal(false);
  };

  return (
    <div className="row">
      {isRecording ? (
        <>
          <Button
            type="default"
            onClick={stopRecording}
            text="Stop"
            fontAwesomeIcon={faPauseCircle}
            label="Stop"
          />
          <ConfirmationModal
            isOpen={showConfirmationModal}
            onConfirm={handleConfirmationModalConfirm}
            onCancel={handleConfirmationModalCancel}
            confirmationType="submit"
            confirmationMessage={endConfirmationMessage!}
          />
        </>
      ) : (
        <>
          <Button
            type="go"
            onClick={startRecording}
            text="Start"
            fontAwesomeIcon={faMicrophone}
            label="Start"
          />
          <ConfirmationModal
            isOpen={showConfirmationModal}
            onConfirm={handleConfirmationModalConfirm}
            onCancel={handleConfirmationModalCancel}
            confirmationType="submit"
            confirmationMessage={startConfirmationMessage!}
          />
        </>
      )}
    </div>
  );
};

export default AudioRecorder;
